// BM-H398 - question primitives shared by the section components.
//
// Every question renders through QuestionRow, which fetches the question's
// own tooltip from tooltipRegistry by question id and, where the engine reads
// the field, offers "Not known" as a first-class answer. No colours and no
// font family are set here: the FormShell surface (BM-H581) owns both.

import FieldTooltip from "../FieldTooltip";
import { getTooltip, hasTooltip } from "./tooltipRegistry";
import { isNotKnown, setNotKnown } from "./willsPayload";

const IS_PRODUCTION = typeof process !== "undefined" && process.env && process.env.NODE_ENV === "production";

export const inputStyle = {
  width: "100%",
  padding: 10,
  border: "1px solid currentColor",
  borderRadius: 6,
  fontSize: 14,
  boxSizing: "border-box",
};

export const textareaStyle = { ...inputStyle, minHeight: 96, resize: "vertical" };

// A missing explanation must be absent and loud in the logs, never present
// and empty. In dev and test an unregistered id throws; the CI gate makes
// that unreachable in a shipped build.
export function tooltipFor(qid) {
  if (!hasTooltip(qid)) {
    if (IS_PRODUCTION) {
      console.error(`tooltipRegistry: no tooltip registered for question ${qid}`);
      return null;
    }
    return getTooltip(qid);
  }
  return getTooltip(qid);
}

// Bind a section object to a setter and hand components the four helpers
// they need. `setSection` receives the whole next section object.
export function sectionProps(section, setSection) {
  return {
    s: section,
    set: (patch) => setSection({ ...section, ...patch }),
    nk: (field) => isNotKnown(section, field),
    setNk: (field, on) => setSection(setNotKnown(section, field, on)),
  };
}

export function QuestionRow({ qid, label, children, notKnown, onNotKnown, sensitive, evidence, hint, testid }) {
  const tooltip = tooltipFor(qid);
  return (
    <div data-testid={testid || `q-${qid}`} data-qid={qid} style={{ marginBottom: 18 }}>
      <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
        {label}
        {tooltip && <FieldTooltip text={tooltip} testid={`tooltip-${qid}`} />}
        {sensitive && <span data-testid={`flag-sensitive-${qid}`} style={flagStyle}>Sensitive</span>}
        {evidence && <span data-testid={`flag-evidence-${qid}`} style={flagStyle}>We may ask for a document</span>}
      </label>
      {hint && <div style={{ fontSize: 13, marginBottom: 6 }}>{hint}</div>}
      <div style={{ opacity: notKnown ? 0.45 : 1 }}>{children}</div>
      {onNotKnown && (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, marginTop: 6 }}>
          <input
            type="checkbox"
            data-testid={`not-known-${qid}`}
            checked={Boolean(notKnown)}
            onChange={(e) => onNotKnown(e.target.checked)}
          />
          Not known, please pick this up with me
        </label>
      )}
    </div>
  );
}

const flagStyle = {
  marginLeft: 8,
  fontSize: 11,
  fontWeight: 600,
  border: "1px solid currentColor",
  borderRadius: 999,
  padding: "1px 8px",
  verticalAlign: "middle",
};

export function TextInput({ value, onChange, testid, type = "text", placeholder, disabled, maxLength }) {
  return (
    <input
      type={type}
      data-testid={testid}
      style={inputStyle}
      value={value ?? ""}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function TextArea({ value, onChange, testid, placeholder, disabled }) {
  return (
    <textarea
      data-testid={testid}
      style={textareaStyle}
      value={value ?? ""}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function NumberInput({ value, onChange, testid, disabled, min = "0", step = "1", prefix }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {prefix && <span>{prefix}</span>}
      <input
        type="number"
        min={min}
        step={step}
        data-testid={testid}
        style={inputStyle}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    </div>
  );
}

export function MoneyInput(props) {
  return <NumberInput {...props} prefix={"\u00a3"} />;
}

export function Select({ value, onChange, options, testid, disabled, placeholder = "Choose an option" }) {
  return (
    <select
      data-testid={testid}
      style={inputStyle}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// Yes / No as a boolean; "Not known" is handled by QuestionRow so the engine
// never sees a third value on a boolean field.
export function YesNo({ value, onChange, testid, disabled, yesLabel = "Yes", noLabel = "No" }) {
  return (
    <div role="radiogroup" data-testid={testid} style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
      {[[true, yesLabel], [false, noLabel]].map(([v, label]) => (
        <label key={String(v)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
          <input
            type="radio"
            data-testid={`${testid}-${v ? "yes" : "no"}`}
            checked={value === v}
            disabled={disabled}
            onChange={() => onChange(v)}
          />
          {label}
        </label>
      ))}
    </div>
  );
}

export function Checkbox({ checked, onChange, testid, label }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "flex-start", gap: 8, fontSize: 14 }}>
      <input type="checkbox" data-testid={testid} checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function SmallButton({ onClick, children, testid, disabled }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        border: "1px solid currentColor",
        background: "transparent",
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

export function Card({ children, testid }) {
  return (
    <div data-testid={testid} style={{ border: "1px solid currentColor", borderRadius: 10, padding: 14, marginBottom: 12 }}>
      {children}
    </div>
  );
}

export function SectionIntro({ title, children }) {
  return (
    <>
      <h2 style={{ fontSize: 22, margin: "0 0 8px" }}>{title}</h2>
      {children && <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>{children}</p>}
    </>
  );
}

// Repeating rows share one question id per field; the row index only
// distinguishes the test ids.
export function RepeatList({ items, onAdd, onRemove, addLabel, testid, render, emptyText }) {
  return (
    <div data-testid={testid}>
      {items.length === 0 && emptyText && <p style={{ fontSize: 13 }}>{emptyText}</p>}
      {items.map((item, i) => (
        <Card key={item.id || i} testid={`${testid}-row-${i}`}>
          {render(item, i)}
          <div style={{ marginTop: 8 }}>
            <SmallButton testid={`${testid}-remove-${i}`} onClick={() => onRemove(i)}>Remove</SmallButton>
          </div>
        </Card>
      ))}
      <SmallButton testid={`${testid}-add`} onClick={onAdd}>{addLabel}</SmallButton>
    </div>
  );
}

export function replaceAt(list, i, next) {
  return list.map((x, j) => (j === i ? next : x));
}

export function removeAt(list, i) {
  return list.filter((_, j) => j !== i);
}
